import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import useAdd from '../../api/useAdd';
import useFetchObjects from '../../api/useFetchObjects';
import { useAuth } from '../../context/AuthContext';

const DepartmentCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    directorate_id: '',
    manager_user_id: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});
  
  // Fetch directorates
  const { 
    data: directoratesData, 
    isLoading: loadingDirectorates 
  } = useFetchObjects('directorates', {}, token);
  
  const directorates = Array.isArray(directoratesData) ? directoratesData : (directoratesData?.data || []);

  // Add mutation hook
  const {
    handleAdd,
    loading,
    success
  } = useAdd(
    'departments',
    token,
    '/departments',
    t('departments.create_success', 'Department created successfully'),
    t('departments.create_error', 'Failed to create department')
  );

  // Set current user as manager
  useEffect(() => {
    if (user && user.id) {
      setFormData(prev => ({
        ...prev,
        manager_user_id: user.id
      }));
    }
  }, [user]);

  // Reset form when creation is successful
  useEffect(() => {
    if (success) {
      setFormData({
        name: '',
        description: '',
        directorate_id: '',
        manager_user_id: user?.id || '',
      });
      setErrors({});
    }
  }, [success, user]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required', 'This field is required');
    }
    
    if (!formData.directorate_id) {
      newErrors.directorate_id = t('validation.required', 'This field is required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Submit form data
    handleAdd(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={RouterLink}
          to="/departments"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Typography variant="h4" component="h1">
          {t('departments.create_title', 'Create New Department')}
        </Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('departments.basic_info', 'Basic Information')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label={t('departments.name', 'Department Name')}
                placeholder={t('departments.name_placeholder', 'Enter department name')}
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label={t('departments.description', 'Description')}
                placeholder={t('departments.description_placeholder', 'Enter department description')}
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.directorate_id} required>
                <InputLabel id="directorate-label">
                  {t('departments.directorate', 'Directorate')}
                </InputLabel>
                <Select
                  labelId="directorate-label"
                  name="directorate_id"
                  label={t('departments.directorate', 'Directorate')}
                  value={formData.directorate_id}
                  onChange={handleChange}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>{t('common.select', 'Select')}</em>
                  </MenuItem>
                  {loadingDirectorates ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('common.loading', 'Loading...')}
                    </MenuItem>
                  ) : (
                    directorates.map((directorate) => (
                      <MenuItem key={directorate.id} value={directorate.id}>
                        {directorate.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.directorate_id && (
                  <FormHelperText>{errors.directorate_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('departments.head_info', 'You will be assigned as the department manager')}
          </Typography>
              <TextField
                fullWidth
                disabled
                label={t('departments.current_user', 'Current User')}
                value={user?.username || user?.email || ''}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/departments"
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          variant="contained"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t('common.save', 'Save')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default DepartmentCreate;